import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';
import type { JSX } from 'react';
import { formatMm } from '@bw/core';
import { ProjectDocument, Autosaver, serialiseProject } from '@bw/document';
import type { Renderer } from '@bw/render-2d';
import { EXAMPLE_HOUSE } from './examples.js';
import { watchForUpdates } from './updates.js';
import { CanvasStage } from './components/CanvasStage.js';
import { CommandBar } from './components/CommandBar.js';
import { StageBar } from './components/StageBar.js';
import { ToolRail } from './components/ToolRail.js';
import { ToolPanel } from './components/ToolPanel.js';
import { Inspector } from './components/Inspector.js';
import { StatusBar } from './components/StatusBar.js';
import {
  CommandPalette,
  ConfirmDialog,
  HelpDialog,
  LibraryDialog,
  Modal,
} from './components/Dialogs.js';
import { ExportDialog } from './components/ExportDialog.js';
import { EmptyState } from './components/EmptyState.js';
import { View3D } from './components/View3D.js';
import { WallOutline } from './components/WallOutline.js';
import { CanvasContextMenu } from './components/CanvasContextMenu.js';
import { PanelLeftClose, PanelRightClose } from 'lucide-react';
import { inlinePanels, useCoarsePointer, useLayoutMode } from './state/layout.js';
import { assembleBuilding } from '@bw/building';
import { buildCommands, describeKey, targetsTextInput } from './commands.js';
import type { Command } from './commands.js';
import type { EditorController } from './editor/controller.js';
import { solveJointChange } from './editor/joints.js';
import { placementForNewWall } from './editor/walls.js';
import type { WallLoadOutcome } from './editor/files.js';
import {
  RepairNeeded,
  importWallText,
  libraryEntryFor,
  loadProjectText,
  pickAndLoad,
  pickWall,
  saveProject,
  storage,
} from './editor/files.js';
import { COPY, plural } from './copy.js';
import { downloadBytes } from './editor/exporting.js';
import { activeModel, isDirty, useEditor } from './state/store.js';
import { useVolatile } from './state/volatile.js';

type DialogState =
  | { readonly kind: 'none' }
  | { readonly kind: 'help' }
  | { readonly kind: 'palette' }
  | { readonly kind: 'library' }
  | { readonly kind: 'export' }
  | { readonly kind: 'outline' }
  | { readonly kind: 'clear' }
  | { readonly kind: 'deleteWall'; readonly id: string }
  | { readonly kind: 'unsaved'; readonly next: () => void }
  | { readonly kind: 'repair'; readonly detail: RepairNeeded }
  | { readonly kind: 'recover'; readonly text: string }
  | { readonly kind: 'error'; readonly title: string; readonly message: string };

export function App(): JSX.Element {
  const controllerRef = useRef<EditorController | null>(null);
  const rendererRef = useRef<Renderer | null>(null);
  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });
  const [jointNote, setJointNote] = useState<
    { readonly text: string; readonly tone: 'note' | 'warn' | 'ok' } | null
  >(null);
  const readout = useVolatile();
  // Narrow selectors, so a status change — one per brick laid — no longer
  // re-renders the shell (F12).
  const project = useEditor((s) => s.project);
  const docRevision = useEditor((s) => s.docRevision);
  const busy = useEditor((s) => s.busy);
  const leftPanelOpen = useEditor((s) => s.leftPanelOpen);
  const rightPanelOpen = useEditor((s) => s.rightPanelOpen);
  const welcomeDismissed = useEditor((s) => s.welcomeDismissed);
  const view3d = useEditor((s) => s.view3d);
  const setView3D = useEditor((s) => s.setView3D);
  const snapEnabled = useEditor((s) => s.snapEnabled);
  const themePreference = useEditor((s) => s.theme);
  const togglePanel = useEditor((s) => s.togglePanel);
  const dismissWelcome = useEditor((s) => s.dismissWelcome);
  const setSnapEnabled = useEditor((s) => s.setSnapEnabled);
  const layout = useLayoutMode();
  const coarsePointer = useCoarsePointer();
  const [contextMenu, setContextMenu] = useState<
    { readonly x: number; readonly y: number; readonly hasBrick: boolean } | null
  >(null);

  useEffect(() => {
    document.documentElement.dataset['layout'] = layout;
  }, [layout]);

  // The canvas prints its own hint on an empty wall; the welcome card says the
  // same thing over the top of it. Dismissing the card brings the hint back.
  useEffect(() => {
    controllerRef.current?.redraw();
  }, [welcomeDismissed]);

  // A panel that leaves the flow starts closed, because a floating one covers
  // the drawing it is meant to describe: at 900 px both open left barely a
  // fifth of the window drawable. Docking a panel again reopens it, so the
  // rule is simply "inline panels are open" and resizing never strands the
  // user with a workspace they cannot see.
  useEffect(() => {
    const open = inlinePanels(layout);
    useEditor.setState({ leftPanelOpen: open.left, rightPanelOpen: open.right });
  }, [layout]);

  // A coarse pointer closes both regardless — a finger has no hover, so an
  // overlay is that much harder to get out of the way.
  useEffect(() => {
    if (!coarsePointer) return;
    useEditor.setState({ leftPanelOpen: false, rightPanelOpen: false });
  }, [coarsePointer]);
  const autosaver = useMemo(() => new Autosaver(storage, 4000), []);

  const dark = useSystemDark(themePreference);
  useEffect(() => {
    document.documentElement.dataset['theme'] = dark ? 'dark' : 'light';
  }, [dark]);

  // ---- document operations ----

  const guardUnsaved = useCallback(
    (next: () => void): void => {
      if (!isDirty(useEditor.getState())) {
        next();
        return;
      }
      setDialog({ kind: 'unsaved', next });
    },
    [],
  );

  const adoptProject = useCallback(
    (project: ProjectDocument, name: string, handle: FileSystemFileHandle | null, message: string) => {
      const store = useEditor.getState();
      store.setProject(project, { handle, name });
      // The clipboard is gone with the old document, so the paste tool would
      // be armed with nothing (F5).
      if (store.tool === 'paste') store.setTool('select');
      if (project.loadWarnings.length > 0) {
        setDialog({
          kind: 'error',
          title: 'Opened with repairs',
          message: project.loadWarnings.join('\n\n'),
        });
      }
      controllerRef.current?.afterDocumentChange(true);
      controllerRef.current?.setStatus(message, true);
      setJointNote(null);
    },
    [],
  );

  const newProject = useCallback(() => {
    guardUnsaved(() => {
      adoptProject(new ProjectDocument('Untitled'), 'Untitled', null, COPY.newProject);
    });
  }, [adoptProject, guardUnsaved]);

  const openProject = useCallback(() => {
    guardUnsaved(() => {
      void (async () => {
        try {
          const picked = await pickAndLoad();
          if (picked === null) return;
          adoptProject(
            picked.outcome.project,
            picked.name,
            picked.handle,
            picked.outcome.droppedBricks > 0
              ? COPY.imported(picked.outcome.project.brickCount, picked.outcome.droppedBricks)
              : COPY.opened(picked.name),
          );
        } catch (error) {
          if (error instanceof RepairNeeded) setDialog({ kind: 'repair', detail: error });
          else
            setDialog({
              kind: 'error',
              title: 'Could not open project',
              message: error instanceof Error ? error.message : String(error),
            });
        }
      })();
    });
  }, [adoptProject, guardUnsaved]);

  /**
   * Open the example that ships with the app.
   *
   * No file handle goes with it: it is a read-only thing on the server, so
   * saving has to ask where to put it rather than quietly writing over the
   * copy the next visitor gets.
   */
  const openExample = useCallback(() => {
    guardUnsaved(() => {
      void (async () => {
        // Downloading and reading are separate failures with separate causes,
        // so they are caught separately: a browser's own message for a failed
        // fetch is "Failed to fetch", which tells nobody anything.
        let text: string;
        try {
          const response = await fetch(EXAMPLE_HOUSE.path);
          if (!response.ok) throw new Error(COPY.exampleRefused(response.status));
          text = await response.text();
        } catch (error) {
          setDialog({
            kind: 'error',
            title: 'Could not open the example',
            message: error instanceof Error && error.message !== '' && !(error instanceof TypeError)
              ? error.message
              : COPY.exampleOffline,
          });
          return;
        }

        try {
          const outcome = loadProjectText(text, EXAMPLE_HOUSE.name);
          adoptProject(
            outcome.project,
            outcome.project.name,
            null,
            COPY.opened(outcome.project.name),
          );
        } catch (error) {
          setDialog({
            kind: 'error',
            title: 'Could not open the example',
            message: error instanceof Error ? error.message : String(error),
          });
        }
      })();
    });
  }, [adoptProject, guardUnsaved]);

  /**
   * Add one wall from the desktop tool to the project already open.
   *
   * Not `openProject` with a different loader: nothing is replaced, so there
   * are no unsaved changes to guard and no file handle to take over — this is
   * an edit to the current document, and it undoes like one.
   */
  const importWall = useCallback((text: string, fileName: string, repair: boolean): void => {
    const store = useEditor.getState();
    // Measured from the wall that is still active, exactly as adding one is,
    // so four elevations imported in order walk round the outline.
    const placement = placementForNewWall(store.project);
    store.history.checkpoint(store.project);
    let outcome: WallLoadOutcome;
    try {
      outcome = importWallText(store.project, text, fileName, placement, repair);
    } catch (error) {
      // The import leaves the document untouched when it refuses, so the
      // checkpoint has nothing to go back to and would be an empty undo step.
      store.history.abandon();
      if (error instanceof RepairNeeded) setDialog({ kind: 'repair', detail: error });
      else
        setDialog({
          kind: 'error',
          title: 'Could not import the wall',
          message: error instanceof Error ? error.message : String(error),
        });
      return;
    }
    store.setSelected(new Set());
    store.touch();
    controllerRef.current?.afterDocumentChange(true);
    controllerRef.current?.setStatus(
      COPY.wallImported(outcome.wall.name, outcome.wall.model.size, outcome.droppedBricks),
      true,
    );
  }, []);

  const pickAndImportWall = useCallback(() => {
    void (async () => {
      const picked = await pickWall();
      if (picked === null) return;
      importWall(picked.text, picked.name, false);
    })();
  }, [importWall]);

  const doSave = useCallback(async (saveAs: boolean) => {
    const store = useEditor.getState();
    try {
      const result = await saveProject(store.project, saveAs ? null : store.file?.handle ?? null);
      if (result === null) return;
      useEditor.setState({ file: { handle: result.handle, name: result.name } });
      store.markSaved();
      store.setLibrary(await storage.listLibrary());
      controllerRef.current?.setStatus(COPY.saved(result.name), true);
      await storage.clearAutosave();
    } catch (error) {
      setDialog({
        kind: 'error',
        title: 'Could not save project',
        message: error instanceof Error ? error.message : String(error),
      });
    }
  }, []);

  const jointGeneration = useRef(0);
  const applyJoint = useCallback(async (value: string) => {
    const store = useEditor.getState();
    // Two quick clicks used to start two solves; the second then committed
    // coordinates computed for the pre-commit wall (F3).
    if (store.busy !== null) return;
    const generation = ++jointGeneration.current;
    const model = activeModel(store);
    store.setBusy('Re-coordinating the wall…');
    try {
      const outcome = await solveJointChange(model, value);
      if (generation !== jointGeneration.current) return;
      if (outcome.error !== null) {
        setJointNote({ text: `${COPY.perpendUnchangedWall} ${outcome.error}`, tone: 'warn' });
        return;
      }
      if (!outcome.changed || outcome.commit === null) {
        setJointNote({ text: COPY.perpendUnchanged(model.perpendGapMm), tone: 'note' });
        return;
      }
      controllerRef.current?.checkpoint();
      try {
        outcome.commit();
      } catch (error) {
        store.history.undo(store.project);
        setJointNote({
          text: `${COPY.perpendUnchangedWall} ${error instanceof Error ? error.message : String(error)}`,
          tone: 'warn',
        });
        return;
      }
      // The clipboard's offsets were captured at the old gap (clause 7.1).
      store.setClipboard(null);
      if (store.tool === 'paste') store.setTool('select');
      store.touch();
      controllerRef.current?.afterDocumentChange();
      setJointNote({
        text: `Moved ${plural(outcome.moved)} to hold the bond at ${formatMm(model.perpendGapMm)} mm.`,
        tone: 'ok',
      });
      controllerRef.current?.setStatus(COPY.perpendApplied(model.perpendGapMm, model.size), true);
    } finally {
      store.setBusy(null);
    }
  }, []);

  // ---- commands ----

  const commands = useMemo<Command[]>(() => {
    const controller = controllerRef.current;
    if (controller === null) return [];
    return buildCommands({
      controller,
      newProject,
      openProject,
      openExample,
      saveProject: (saveAs) => doSave(saveAs),
      showLibrary: () => {
        void storage.listLibrary().then((entries) => {
          useEditor.getState().setLibrary(entries);
          setDialog({ kind: 'library' });
        });
      },
      showHelp: () => setDialog({ kind: 'help' }),
      showExport: () => setDialog({ kind: 'export' }),
      showOutline: () => setDialog({ kind: 'outline' }),
      showPalette: () => setDialog({ kind: 'palette' }),
      addWall: () => {
        const store = useEditor.getState();
        store.history.checkpoint(store.project);
        // Worked out before the wall exists, because it is measured from the
        // wall that is still active: a new wall carries on from the end of the
        // current one rather than landing exactly on top of it.
        const placement = placementForNewWall(store.project);
        const wall = store.project.addWall();
        store.project.setWallPlacement(wall.id, placement);
        store.project.setActiveWall(wall.id);
        store.setSelected(new Set());
        store.touch();
        controllerRef.current?.afterDocumentChange(true);
        controllerRef.current?.setStatus(COPY.wallAdded(wall.name), true);
      },
      importWall: pickAndImportWall,
      deleteWall: (id: string) => setDialog({ kind: 'deleteWall', id }),
      cycleWall: (delta) => {
        const store = useEditor.getState();
        const walls = store.project.walls;
        const index = walls.findIndex((wall) => wall.id === store.project.activeWallId);
        const next = walls[(index + delta + walls.length) % walls.length];
        if (next !== undefined && store.project.setActiveWall(next.id)) {
          store.setSelected(new Set());
          store.touch();
          controllerRef.current?.afterDocumentChange(true);
        }
      },
      confirmClear: () => setDialog({ kind: 'clear' }),
    });
    // Rebuilt when the controller first appears; hooks are stable thereafter.
  }, [doSave, newProject, openProject, openExample, pickAndImportWall, controllerRef.current !== null]);

  const run = useCallback(
    (id: string) => {
      const command = commands.find((candidate) => candidate.id === id);
      if (command === undefined) return;
      if (command.enabled !== undefined && !command.enabled()) return;
      void command.run();
    },
    [commands],
  );

  // ---- keyboard ----

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === ' ' && !targetsTextInput(event.target)) {
        controllerRef.current?.setSpaceHeld(true);
        event.preventDefault();
        return;
      }
      // Alt bypasses snapping for as long as it is held, so the preview has to
      // be recomputed when it goes down rather than only when the mouse next
      // moves — the point under the pointer changes without the pointer.
      if (event.key === 'Alt') {
        controllerRef.current?.refreshPreview(true);
        return;
      }
      if (event.repeat) return;
      const described = describeKey(event);
      const bare = !event.ctrlKey && !event.metaKey && !event.altKey;
      // Clause 10.3's guard: bare letters and digits must not fire while a
      // text field has focus.
      if (bare && targetsTextInput(event.target) && described !== 'Escape') return;
      const match = commands.find(
        (command) => command.shortcut !== undefined && command.shortcut.toLowerCase() === described.toLowerCase(),
      );
      if (match === undefined) return;
      if (match.enabled !== undefined && !match.enabled()) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      void match.run();
    };
    const onKeyUp = (event: KeyboardEvent): void => {
      if (event.key === ' ') controllerRef.current?.setSpaceHeld(false);
      if (event.key === 'Alt') controllerRef.current?.refreshPreview(false);
      // Releasing an arrow closes the coalesced nudge run, so the next one
      // becomes its own undo entry (design document C3).
      if (event.key.startsWith('Arrow')) controllerRef.current?.endNudgeRun();
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [commands]);

  // ---- autosave, recovery and the unload guard ----

  useEffect(() => {
    void (async () => {
      const recovered = await storage.readAutosave();
      if (recovered !== null) setDialog({ kind: 'recover', text: recovered });
      useEditor.getState().setLibrary(await storage.listLibrary());
    })();
  }, []);

  const revision = project.revision;
  useEffect(() => {
    if (!isDirty(useEditor.getState())) return;
    autosaver.schedule(() => serialiseProject(useEditor.getState().project));
    // Keyed on the document alone: including the whole store restarted the
    // debounce on every status change and could starve the write (F14).
  }, [autosaver, revision, docRevision]);

  useEffect(() => {
    const onHide = (): void => {
      if (document.visibilityState === 'hidden') void autosaver.flush();
    };
    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      if (isDirty(useEditor.getState())) {
        void autosaver.flush();
        event.preventDefault();
        event.returnValue = '';
      }
    };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [autosaver]);

  // ---- staying on the build that is deployed ----

  useEffect(
    () =>
      watchForUpdates({
        // Never out from under work in progress: the guard above would meet an
        // automatic reload with the browser's own "leave site?" prompt, which
        // is a worse thing to be handed than an app one version behind. The
        // update is taken at the first moment the answer here changes.
        canReload: () => !isDirty(useEditor.getState()),
      }),
    [],
  );

  const onReady = useCallback((controller: EditorController, renderer: Renderer) => {
    controllerRef.current = controller;
    rendererRef.current = renderer;
    if (import.meta.env.DEV) {
      void import('./debug.js').then((m) => m.attachDebugTargets(controller, renderer));
    }
    controller.restStatus();
    controller.onContextMenu((point) => {
      const model = activeModel(useEditor.getState());
      const viewport = renderer.viewport;
      const brick = model.hitTest(viewport.toWorldX(point.x), viewport.toWorldY(point.y));
      setContextMenu({ x: point.x, y: point.y, hasBrick: brick !== undefined });
    });
    // Force a rebuild of the command list now that the controller exists.
    useEditor.getState().touch();
  }, []);

  /**
   * Keyboard operation of the drawing surface (design document C8): arrows
   * move the keyboard cursor, Enter lays, Tab steps through the wall in course
   * order, and Tab past either end lets focus leave — never a keyboard trap.
   */
  const onCanvasKeyDown = useCallback((event: React.KeyboardEvent<HTMLElement>) => {
    const controller = controllerRef.current;
    if (controller === null) return;
    const store = useEditor.getState();
    const modifiers = { shift: event.shiftKey, alt: event.altKey, ctrl: event.ctrlKey || event.metaKey };

    if (event.key === 'Tab') {
      if (controller.cycleSelection(event.shiftKey)) event.preventDefault();
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      controller.layAtKeyboardCursor();
      return;
    }
    const direction =
      event.key === 'ArrowLeft' ? 'left'
      : event.key === 'ArrowRight' ? 'right'
      : event.key === 'ArrowUp' ? 'up'
      : event.key === 'ArrowDown' ? 'down'
      : null;
    if (direction === null) return;
    event.preventDefault();
    event.stopPropagation();
    // With a selection the arrows nudge it; otherwise they drive the cursor.
    if (store.selected.size > 0 && store.tool !== 'build') controller.nudge(direction, modifiers);
    else if (store.tool === 'build') controller.moveKeyboardCursor(direction, modifiers);
    else if (store.selected.size > 0) controller.nudge(direction, modifiers);
  }, []);

  const onSelectWall = useCallback((id: string) => {
    const store = useEditor.getState();
    if (!store.project.setActiveWall(id)) return;
    store.setSelected(new Set());
    store.touch();
    controllerRef.current?.afterDocumentChange(true);
  }, []);

  const inline = inlinePanels(layout);

  return (
    <div className="app">
      <CommandBar run={run} commands={commands} zoomText={readout.zoom} />
      <StageBar controller={controllerRef.current} run={run} />
      <div className="body">
        <ToolRail run={run} />
        {inline.left && leftPanelOpen && (
          <ToolPanel run={run} controller={controllerRef.current} />
        )}
        <div style={{ position: 'relative', display: 'flex', flex: 1, minWidth: 0 }}>
          <CanvasStage onReady={onReady} dark={dark} onCanvasKeyDown={onCanvasKeyDown} />
          {!inline.left && leftPanelOpen && (
            <ToolPanel
              run={run}
              controller={controllerRef.current}
              floating
              onClose={() => togglePanel('left')}
            />
          )}
          {!inline.right && rightPanelOpen && (
            <Inspector
              onApplyJoint={(value) => void applyJoint(value)}
              jointNote={jointNote}
              run={run}
              onSelectWall={onSelectWall}
              onDeleteWall={(id) => setDialog({ kind: 'deleteWall', id })}
              floating
              onClose={() => togglePanel('right')}
            />
          )}
          {/* An open floating panel carries its own close button, so the edge
              toggle would only ever sit underneath it. Inline panels keep
              theirs at all times — otherwise closing one at full width leaves
              no way to bring it back. */}
          {(inline.left || !leftPanelOpen) && (
            <div className="paneltoggle left">
              <button
                aria-label={leftPanelOpen ? 'Hide tool options' : 'Show tool options'}
                aria-pressed={leftPanelOpen}
                onClick={() => togglePanel('left')}
              >
                <PanelLeftClose size={16} />
              </button>
            </div>
          )}
          {(inline.right || !rightPanelOpen) && (
            <div className="paneltoggle right">
              <button
                aria-label={rightPanelOpen ? 'Hide inspector' : 'Show inspector'}
                aria-pressed={rightPanelOpen}
                onClick={() => togglePanel('right')}
              >
                <PanelRightClose size={16} />
              </button>
            </div>
          )}
          {project.activeWall.model.size === 0 && !welcomeDismissed && (
            <EmptyState run={run} onDismiss={dismissWelcome} />
          )}
          {busy !== null && <div className="busy">{busy}</div>}
          {contextMenu !== null && (
            <CanvasContextMenu
              x={contextMenu.x}
              y={contextMenu.y}
              hasBrick={contextMenu.hasBrick}
              snapEnabled={snapEnabled}
              onClose={() => setContextMenu(null)}
              onSelectOnly={() => controllerRef.current?.runToolAt(contextMenu, false)}
              onAdd={() => controllerRef.current?.addToSelectionAt(contextMenu)}
              onToggle={() => controllerRef.current?.toggleSelectionAt(contextMenu)}
              onDelete={() => controllerRef.current?.deleteSelection()}
              onToggleSnap={() => {
                setSnapEnabled(!snapEnabled);
                controllerRef.current?.refreshPreview();
              }}
            />
          )}
        </div>
        {inline.right && rightPanelOpen && (
          <Inspector
            onApplyJoint={(value) => void applyJoint(value)}
            jointNote={jointNote}
            run={run}
            onSelectWall={onSelectWall}
            onDeleteWall={(id) => setDialog({ kind: 'deleteWall', id })}
          />
        )}
      </div>
      <StatusBar />
      {view3d && (
        <View3D
          building={assembleBuilding(project)}
          colourOf={(id) => project.material(id)?.colour ?? project.materials[0]?.colour ?? '#B5523B'}
          roughnessOf={(id) => project.material(id)?.roughness ?? null}
          roofColour={project.material(project.roof.materialId)?.colour ?? '#6E5B4B'}
          frameColour={project.joinery.frameColour}
          dark={dark}
          onClose={() => setView3D(false)}
          onPickBrick={(wallId, brickId) => {
            // The whole point of a review view: land on the brick you clicked,
            // selected and in view, in the drawing you edit.
            const store = useEditor.getState();
            if (store.project.activeWallId !== wallId) {
              store.project.setActiveWall(wallId);
              store.touch();
              controllerRef.current?.afterDocumentChange(false);
            }
            setView3D(false);
            store.setTool('select');
            controllerRef.current?.focusBrick(brickId as never);
          }}
          onSaveImage={(blob) => {
            void blob.arrayBuffer().then((buffer) => {
              const name = `${project.name.trim() === '' ? 'Building' : project.name.trim()}.png`;
              downloadBytes(name, new Uint8Array(buffer), 'image/png');
              controllerRef.current?.setStatus(COPY.savedImage(name), true);
            });
          }}
        />
      )}
      <Dialogs
        dialog={dialog}
        setDialog={setDialog}
        commands={commands}
        run={run}
        adoptProject={adoptProject}
        importWall={importWall}
        doSave={doSave}
        controllerRef={controllerRef}
      />
    </div>
  );
}

function Dialogs({
  dialog,
  setDialog,
  commands,
  run,
  adoptProject,
  importWall,
  doSave,
  controllerRef,
}: {
  readonly dialog: DialogState;
  readonly setDialog: (next: DialogState) => void;
  readonly commands: readonly Command[];
  readonly run: (id: string) => void;
  readonly adoptProject: (
    project: ProjectDocument,
    name: string,
    handle: FileSystemFileHandle | null,
    message: string,
  ) => void;
  readonly importWall: (text: string, fileName: string, repair: boolean) => void;
  readonly doSave: (saveAs: boolean) => Promise<void>;
  readonly controllerRef: { current: EditorController | null };
}): JSX.Element | null {
  const library = useEditor((store) => store.library);
  const close = (): void => setDialog({ kind: 'none' });

  switch (dialog.kind) {
    case 'help':
      return <HelpDialog onClose={close} />;
    case 'outline':
      return (
        <WallOutline onClose={close} onFocusBrick={(id) => controllerRef.current?.focusBrick(id)} />
      );
    case 'export':
      return (
        <ExportDialog
          onClose={close}
          onStatus={(text) => controllerRef.current?.setStatus(text, true)}
        />
      );
    case 'palette':
      return <CommandPalette commands={commands} onClose={close} onRun={run} />;
    case 'library':
      return (
        <LibraryDialog
          entries={library}
          onClose={close}
          onOpen={(id) => {
            void (async () => {
              const text = await storage.getLibrary(id);
              if (text === null) return;
              close();
              const outcome = loadProjectText(text, id);
              adoptProject(outcome.project, outcome.project.name, null, COPY.opened(outcome.project.name));
            })();
          }}
          onDelete={(id) => {
            void (async () => {
              await storage.deleteLibrary(id);
              useEditor.getState().setLibrary(await storage.listLibrary());
            })();
          }}
        />
      );
    case 'deleteWall':
      return <DeleteWallDialog id={dialog.id} onClose={close} controllerRef={controllerRef} />;
    case 'clear':
      return (
        <ConfirmDialog
          title="Clear wall"
          message={<p>Remove every brick from this wall? You can undo this action.</p>}
          confirmLabel="Clear wall"
          onCancel={close}
          onConfirm={() => {
            close();
            controllerRef.current?.clearWall();
          }}
        />
      );
    case 'unsaved':
      return (
        <ConfirmDialog
          title="Unsaved changes"
          message={<p>Save changes to this project before continuing?</p>}
          confirmLabel="Save"
          extraLabel="Discard"
          onExtra={() => {
            close();
            dialog.next();
          }}
          onCancel={close}
          onConfirm={() => {
            void (async () => {
              await doSave(false);
              close();
              dialog.next();
            })();
          }}
        />
      );
    case 'repair':
      return (
        <ConfirmDialog
          title="Some bricks could not be placed"
          message={
            <>
              <p>
                {plural(dialog.detail.detail.problems.length)} in this file could not be placed with
                valid clearance. The rest of the wall is fine.
              </p>
              <ul>
                {dialog.detail.detail.problems.slice(0, 6).map((problem) => (
                  <li key={problem.index}>
                    Brick #{problem.id ?? problem.index} · {problem.kind} at X {formatMm(problem.x)}, Y{' '}
                    {formatMm(problem.y)} mm
                  </li>
                ))}
              </ul>
            </>
          }
          confirmLabel={dialog.detail.target === 'wall' ? 'Import without them' : 'Open without them'}
          onCancel={close}
          onConfirm={() => {
            close();
            if (dialog.detail.target === 'wall') {
              importWall(dialog.detail.text, dialog.detail.fileName, true);
              return;
            }
            const outcome = loadProjectText(dialog.detail.text, dialog.detail.fileName, true);
            adoptProject(
              outcome.project,
              dialog.detail.fileName,
              null,
              COPY.imported(outcome.project.brickCount, outcome.droppedBricks),
            );
          }}
        />
      );
    case 'recover':
      return (
        <ConfirmDialog
          title="Recover unsaved work"
          message={<p>A project was left unsaved when this tab last closed. Restore it?</p>}
          confirmLabel="Restore"
          extraLabel="Discard"
          onExtra={() => {
            void storage.clearAutosave();
            close();
          }}
          onCancel={close}
          onConfirm={() => {
            close();
            try {
              const outcome = loadProjectText(dialog.text, 'Recovered');
              adoptProject(outcome.project, outcome.project.name, null, 'Recovered unsaved work.');
            } catch {
              void storage.clearAutosave();
            }
          }}
        />
      );
    case 'error':
      return (
        <Modal title={dialog.title} onClose={close}>
          <p>{dialog.message}</p>
        </Modal>
      );
    default:
      return null;
  }
}

/**
 * Deleting a wall takes its bricks with it, so it says how many first.
 *
 * Its own component because it has to read the live document for the name and
 * the count: the dialog state carries an id, not a snapshot, so a wall renamed
 * behind the dialog is still named correctly in it.
 */
function DeleteWallDialog({
  id,
  onClose,
  controllerRef,
}: {
  readonly id: string;
  readonly onClose: () => void;
  readonly controllerRef: { current: EditorController | null };
}): JSX.Element | null {
  const project = useEditor((store) => store.project);
  useEditor((store) => store.docRevision);
  const wall = project.walls.find((candidate) => candidate.id === id);
  if (wall === undefined) return null;
  return (
    <ConfirmDialog
      title={`Delete ${wall.name}`}
      message={
        <p>
          {wall.model.size === 0
            ? 'This wall is empty.'
            : `The ${plural(wall.model.size)} laid on this wall will go with it.`}{' '}
          You can undo this action.
        </p>
      }
      confirmLabel="Delete wall"
      onCancel={onClose}
      onConfirm={() => {
        onClose();
        const store = useEditor.getState();
        store.history.checkpoint(store.project);
        if (!store.project.removeWall(id)) return;
        store.setSelected(new Set());
        store.touch();
        controllerRef.current?.afterDocumentChange(true);
        controllerRef.current?.setStatus(COPY.wallDeleted(wall.name), true);
      }}
    />
  );
}

function useSystemDark(preference: 'light' | 'dark' | 'system'): boolean {
  const [systemDark, setSystemDark] = useState(
    () => typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches,
  );
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const query = matchMedia('(prefers-color-scheme: dark)');
    const onChange = (): void => setSystemDark(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return preference === 'system' ? systemDark : preference === 'dark';
}
